import { DocumentNode } from '@apollo/client';

import { toastNotify } from 'graphql/hooks/common';
import { topRightBoom } from 'services/common/confetti';
import { parseCacheFields } from 'services/common/dataProcessingHelpers';
import client, { cache } from '../client';

import { EVENT_SECTION, TEMPLATE_SECTION, FAVORITE_SECTION } from '../fragments/sections';

export const getSectionCache = (sectionId: string, fragment: DocumentNode) => {
  if (!sectionId || !fragment) {
    return null;
  }

  const cachedSection = client.readFragment({
    id: sectionId,
    fragment,
  });

  return cachedSection;
};

export const updateTimelineSectionCache = <T extends {}>(fragment: DocumentNode) => (
  sectionId: string,
  payload: Partial<T>
): void => {
  const cachedSection = getSectionCache(sectionId, fragment);

  if (!!cachedSection) {
    cache.modify({
      id: sectionId,
      fields: parseCacheFields(payload),
    });
  }
};

export const updateEventSectionCache = updateTimelineSectionCache(EVENT_SECTION);
export const updateTemplateSectionCache = updateTimelineSectionCache(TEMPLATE_SECTION);
export const updateFavoriteSectionCache = updateTimelineSectionCache(FAVORITE_SECTION);

export const updateSectionProgress = (
  sectionId: string,
  res: TypeQuestionProgress | TypeSongProgress
): void => {
  const progress = res as TypeQuestionProgress & TypeSongProgress;
  const cachedSection = client.readFragment({
    id: sectionId,
    fragment: EVENT_SECTION,
  });

  if (!!cachedSection) {
    if (cachedSection.totalProgress < 1 && progress?.totalProgress === 1) {
      toastNotify({ text: 'Section progress 100%' });
      topRightBoom();
    }

    cache.modify({
      id: sectionId,
      fields: {
        totalProgress: () => progress?.totalProgress,
        answeredCount: () => progress?.answeredCount,
        progress: () => progress?.progress,
      },
    });
  }
};

export const getEventSection = (sectionId: string): Nullable<EventSection> => {
  return client.readFragment<EventSection>({
    id: sectionId,
    fragment: EVENT_SECTION,
    fragmentName: 'EventSection',
  });
};

export const getTemplateSection = (sectionId: string): Nullable<TemplateSection> => {
  return client.readFragment<TemplateSection>({
    id: sectionId,
    fragment: TEMPLATE_SECTION,
    fragmentName: 'TemplateSection',
  });
};

export const getFavoriteSection = (sectionId: string): Nullable<FavoriteSectionOption> => {
  return client.readFragment<FavoriteSectionOption>({
    id: sectionId,
    fragment: FAVORITE_SECTION,
    fragmentName: 'FavoriteSection',
  });
};

export const getSection = (
  sectionId: string,
  isEventSection: boolean,
  isFavorite?: boolean
): Nullable<EventSection | TemplateSection | FavoriteSectionOption> => {
  return isFavorite
    ? getFavoriteSection(sectionId)
    : isEventSection
    ? getEventSection(sectionId)
    : getTemplateSection(sectionId);
};
